import ActivityEntry from 'components/activity/ActivityEntry';
import { parseCalendarDate } from 'helper/helper';
import {
  actorContext,
  actorName,
  describeAction,
  isDestructive,
  visibleChanges,
} from 'helper/timeSheetEventLabels';

const DAY_CHIP_FORMAT = { weekday: 'short', day: 'numeric', month: 'short' };

/** "Lun. 13 févr." — `dayRef.name` holds the sheet's own shorthand ("Sa 8"),
 *  which loses the month once a timeline spans two of them. */
const formatDayChip = (day) => {
  if (!day?.date) return day?.name || '';
  const label = parseCalendarDate(day.date).toLocaleDateString(
    'fr-FR',
    DAY_CHIP_FORMAT
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** The stored actor is a single display name, while the avatar wants the two
 *  halves apart to build its initials. */
const personOf = (event) => {
  const name = event?.actor?.displayName;
  if (!name) return null;
  const [firstname, ...rest] = name.trim().split(/\s+/);
  return { firstname, lastname: rest.join(' ') };
};

const DayChip = ({ day }) => (
  <span className="text-xs text-gray-600 px-1.5 py-0.5 border border-gray-200 rounded-md">
    {formatDayChip(day)}
  </span>
);

/** One field that moved, laid out as the mission timeline lays it out: the
 *  field named above, the old value struck through in red, the new one in
 *  green. A derived field is a consequence rather than a decision, so only its
 *  name is demoted — the values still carry the same colours. */
const ChangeBlock = ({ change, muted, last }) => (
  <div
    className={`flex flex-col gap-2 px-3 py-2 text-xs ${
      last ? '' : 'border-b border-gray-100'
    }`}
  >
    <span
      className={`font-medium ${muted ? 'text-gray-400' : 'text-gray-700'}`}
    >
      {change.text || change.label}
    </span>
    {change.text ? null : (
      <div className="flex items-center gap-2 flex-wrap">
        {change.isCreation ? null : (
          <>
            <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded line-through">
              {change.before}
            </span>
            <span className="text-gray-400">→</span>
          </>
        )}
        {change.isRemoval ? (
          <span className="bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded">
            retiré
          </span>
        ) : (
          <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
            {change.after}
          </span>
        )}
      </div>
    )}
  </div>
);

const ChangeCard = ({ decided, derived }) => {
  const changes = [
    ...decided.map((change) => ({ change, muted: false })),
    ...derived.map((change) => ({ change, muted: true })),
  ];
  if (changes.length === 0) return null;

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      {changes.map(({ change, muted }, i) => (
        <ChangeBlock
          key={i}
          change={change}
          muted={muted}
          last={i === changes.length - 1}
        />
      ))}
    </div>
  );
};

/**
 * One gesture on a time sheet, rendered like every other activity timeline in
 * the admin.
 *
 * `events` is usually a single event. The store writes one per day, so filling
 * a week arrives as five identical lines; the list hands them over together and
 * they read as one action spanning several days. When those days carry diffs
 * each one is labelled, otherwise the days show as a row of chips — naming them
 * twice would say nothing more.
 */
const TimeSheetHistoryEntry = ({ event, events }) => {
  const list = events?.length ? events : [event];
  const head = list[0];
  const blocks = list.map((item) => ({
    key: item?._id,
    day: item?.dayRef,
    ...visibleChanges(item),
  }));

  const days = blocks.map((block) => block.day).filter(Boolean);
  const hasDiff = blocks.some(
    (block) => block.decided.length > 0 || block.derived.length > 0
  );
  const perDay = blocks.length > 1 && hasDiff;

  const destructive = isDestructive(head);
  const replacedId = head?.replaces?.id_time_sheet;
  const person = personOf(head);
  const context = actorContext(head);

  return (
    <ActivityEntry
      person={person}
      displayName={actorName(head)}
      date={head?.createdAt}
      fallbackIcon={person ? undefined : 'system'}
    >
      <div className="flex flex-col gap-2">
        <div
          className={`ft-sm ${destructive ? 'text-amber-700' : 'text-gray-600'}`}
        >
          {describeAction(head, days.length || 1)}
          {context ? (
            <span className="text-xs text-gray-400"> · {context}</span>
          ) : null}
        </div>

        {!perDay && days.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {days.map((day) => (
              <DayChip key={day._id || day.date} day={day} />
            ))}
          </div>
        ) : null}

        {replacedId ? (
          <div className="ft-sm text-amber-700">
            Ce relevé remplace le relevé n°{replacedId}
          </div>
        ) : null}

        {perDay ? (
          <div className="flex flex-col gap-2">
            {blocks.map((block) => (
              <div key={block.key} className="flex flex-col gap-1">
                {/* The chip sits in its own row so it keeps its content width
                    while the card below spans the entry. */}
                {block.day ? (
                  <div className="flex">
                    <DayChip day={block.day} />
                  </div>
                ) : null}
                <ChangeCard decided={block.decided} derived={block.derived} />
              </div>
            ))}
          </div>
        ) : (
          <ChangeCard decided={blocks[0].decided} derived={blocks[0].derived} />
        )}
      </div>
    </ActivityEntry>
  );
};

export default TimeSheetHistoryEntry;
